import React, { useEffect, useMemo } from "react";
import { getPositions } from "../helpers/resume";
import { WorkPosition } from "../models/workPosition";
import { setRandomBackground } from "../helpers/backgroundHelpers";

const sectionStyle: React.CSSProperties = { marginTop: "2.5rem" };
const h2Style: React.CSSProperties = { margin: 0, fontSize: "1.4rem" };

const Resume: React.FC = () => {
  const positions = useMemo<WorkPosition[]>(() => getPositions(), []);

  useEffect(() => {
    setRandomBackground();
  }, []);

  return (
    <div
      style={{
        padding: "2rem 1.25rem 4rem",
        maxWidth: 1000,
        margin: "0 auto",
        color: "#fff",
        fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        lineHeight: 1.45,
      }}
    >
      <header style={{ marginBottom: "2rem" }}>
        <h1 style={{ margin: 0, fontSize: "2.4rem", letterSpacing: "0.5px" }}>
          Resume
        </h1>
        <p style={{ opacity: 0.8, margin: "0.5rem 0 0" }}>
          Senior Software Engineer with a track record of building performant,
          maintainable platforms and user experiences across large-scale
          products.
        </p>
      </header>

      <section style={sectionStyle} aria-labelledby="exp-heading">
        <h2 id="exp-heading" style={h2Style}>
          Experience
        </h2>
        <ol
          style={{
            listStyle: "none",
            padding: 0,
            margin: "1.25rem 0 0",
            display: "flex",
            flexDirection: "column",
            gap: "1.75rem",
          }}
        >
          {positions.map((p, i) => {
            return (
              <li
                key={`${p.company}-${p.title}-${i}`}
                style={{
                  background: "rgba(255,255,255,0.05)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 12,
                  padding: "1.1rem 1.2rem 1.15rem",
                  backdropFilter: "blur(4px)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "0.75rem 1rem",
                    alignItems: "baseline",
                  }}
                >
                  <h3
                    style={{
                      margin: 0,
                      fontSize: "1.15rem",
                      fontWeight: 600,
                      letterSpacing: 0.3,
                    }}
                  >
                    {p.title}
                  </h3>
                  <span style={{ opacity: 0.85 }}>{p.company}</span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: "0.8rem",
                      letterSpacing: 0.5,
                      opacity: 0.7,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {p.start} – {p.end}
                  </span>
                </div>
                <ul
                  style={{
                    margin: "0.6rem 0 0",
                    paddingLeft: "1.1rem",
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.35rem",
                  }}
                >
                  {p.descriptions.map((d, idx) => (
                    <li key={idx} style={{ opacity: 0.9 }}>
                      {d}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ol>
      </section>

      <section style={sectionStyle} aria-labelledby="skills-heading">
        <h2 id="skills-heading" style={h2Style}>
          Core Skills
        </h2>
        <p style={{ marginTop: "0.9rem", opacity: 0.9 }}>
          C# · Java · Python · JavaScript · TypeScript · React · Node.js · REST
          / GraphQL · CI/CD · Testing (Jest, RTL) · Performance Optimization ·
          Accessibility · Cloud / DevOps Collaboration
        </p>
      </section>

      <section style={sectionStyle} aria-labelledby="contact-heading">
        <h2 id="contact-heading" style={h2Style}>
          Contact
        </h2>
        <p style={{ marginTop: "0.9rem", opacity: 0.85 }}>
          Reach out via the Contact page or email:{" "}
          <a href="mailto:robyn@robynseverson.com">robyn@robynseverson.com</a>
        </p>
      </section>

      {/* Optional PDF embed example:
        <object data="/resume.pdf" type="application/pdf" width="100%" height="800px">
          <p>Your browser does not support embedded PDFs. <a href="/resume.pdf">Download the PDF</a>.</p>
        </object>
      */}
    </div>
  );
};

export default Resume;
